import { GameState } from './GameState';
import { EconomyEngine } from './EconomyEngine';
import { EventBus } from './EventBus';
import { QUESTS, QUESTS_BY_ID, DAILY_POOL, WEEKLY_POOL } from '../data/quests';
import type { Quest } from '../types';

function ensureProgress(questId: string): void {
  const exists = GameState.data.questProgress.find(p => p.questId === questId);
  if (!exists) {
    GameState.data.questProgress.push({ questId, current: 0, claimed: false });
  }
}

export const QuestEngine = {
  /** 게임 진행 이벤트 기록 — 모든 관련 quest 진행도 갱신 */
  recordEvent(metric: Quest['goal']['metric'], current: number): void {
    // 모든 quest (achievement + daily + weekly) 검사
    const allQuests = [
      ...QUESTS,
      ...DAILY_POOL.map(q => ({ ...q })) as Quest[],
      ...WEEKLY_POOL.map(q => ({ ...q })) as Quest[],
    ];
    for (const q of allQuests) {
      if (q.goal.metric !== metric) continue;
      ensureProgress(q.id);
      const prog = GameState.data.questProgress.find(p => p.questId === q.id)!;
      // metric별 의미: outfits_owned는 누적이 아닌 현재값
      if (q.goal.metric === 'outfits_owned' || q.goal.metric === 'pets_owned' || q.goal.metric === 'pet_happiness_max') {
        prog.current = Math.max(prog.current, current);
      } else {
        prog.current = current;
      }
    }
    GameState.persist();
  },

  /** 진행도 직접 갱신 (record 메서드보다 raw) */
  setProgress(questId: string, value: number): void {
    ensureProgress(questId);
    const prog = GameState.data.questProgress.find(p => p.questId === questId)!;
    prog.current = value;
    GameState.persist();
  },

  isComplete(questId: string): boolean {
    const q = QUESTS_BY_ID[questId] ?? this.getQuestFromPools(questId);
    if (!q) return false;
    const prog = GameState.data.questProgress.find(p => p.questId === questId);
    if (!prog) return false;
    return prog.current >= q.goal.target && !prog.claimed;
  },

  getQuestFromPools(questId: string): Quest | undefined {
    const found = DAILY_POOL.find(q => q.id === questId) ?? WEEKLY_POOL.find(q => q.id === questId);
    return found as Quest | undefined;
  },

  claim(questId: string): boolean {
    const q = QUESTS_BY_ID[questId] ?? this.getQuestFromPools(questId);
    if (!q) return false;
    const prog = GameState.data.questProgress.find(p => p.questId === questId);
    if (!prog || prog.current < q.goal.target || prog.claimed) return false;
    prog.claimed = true;
    EconomyEngine.grant({ stars: q.reward.stars, coins: q.reward.coins, gems: q.reward.gems });
    if (q.reward.outfitId && !GameState.data.ownedOutfits.some(o => o.outfitId === q.reward.outfitId)) {
      GameState.data.ownedOutfits.push({ outfitId: q.reward.outfitId, acquiredAt: Date.now() });
    }
    GameState.persist();
    EventBus.emit('toast', { message: `🏆 보상 받기: ${q.title}`, kind: 'gold' });
    return true;
  },

  refreshDailyQuests(): void {
    // 기존 daily quests 제거
    GameState.data.questProgress = GameState.data.questProgress.filter(p => !p.questId.startsWith('daily_'));
    // 풀에서 5개 랜덤 선택
    const selected = [...DAILY_POOL].sort(() => Math.random() - 0.5).slice(0, 5);
    for (const q of selected) {
      ensureProgress(q.id);
    }
    GameState.persist();
  },

  refreshWeeklyQuests(): void {
    GameState.data.questProgress = GameState.data.questProgress.filter(p => !p.questId.startsWith('weekly_'));
    const selected = [...WEEKLY_POOL].sort(() => Math.random() - 0.5).slice(0, 3);
    for (const q of selected) ensureProgress(q.id);
    GameState.persist();
  },

  /** 모든 stat 기반 메트릭 한번에 동기화 */
  syncFromStats(): void {
    const s = GameState.data.stats;
    this.recordEvent('outfits_owned', GameState.data.ownedOutfits.length);
    this.recordEvent('outfit_equips', s.outfitEquipCount ?? 0);
    this.recordEvent('pets_owned', GameState.data.ownedPets.length);
    this.recordEvent('rival_wins', s.rivalWins);
    this.recordEvent('math_stages_cleared', GameState.data.mathProgress.filter(p => p.clearedAt).length);
    this.recordEvent('outfits_saved', GameState.data.savedOutfits.length);
    this.recordEvent('days_played', s.daysPlayed);
    this.recordEvent('minigames_played', s.minigamesPlayCount ?? 0);
    const maxHappy = Math.max(0, ...GameState.data.ownedPets.map(p => p.happiness));
    this.recordEvent('pet_happiness_max', maxHappy);
    this.recordEvent('rhythm_game_score', s.minigameBest.rhythm ?? 0);
  },

  /** 완료된 모든 퀘스트를 자동으로 보상 수령 — 호출자 편의 */
  autoClaimAll(): { count: number; rewards: { stars: number; coins: number } } {
    const result = { count: 0, rewards: { stars: 0, coins: 0 } };
    for (const prog of GameState.data.questProgress) {
      const q = QUESTS_BY_ID[prog.questId] ?? this.getQuestFromPools(prog.questId);
      if (!q) continue;
      if (prog.current >= q.goal.target && !prog.claimed) {
        if (this.claim(prog.questId)) {
          result.count++;
          result.rewards.stars += q.reward.stars ?? 0;
          result.rewards.coins += q.reward.coins ?? 0;
        }
      }
    }
    return result;
  },
};
